/**
 * Shared hook for AI summary card container logic
 *
 * Provides common functionality for PlayerSummaryCard and AIScoutingSummaryCard
 * including modal state, clipboard operations, and menu trigger rendering.
 */

import { useCallback, useMemo, useState } from "react";
import * as ExpoClipboard from "expo-clipboard";

import {
  NotesListNotesByPlayerIdQuery,
  PlayerDetailPlayerMetaFragment,
} from "@/generated/graphql";
import { useToastNotification } from "@/hooks/useToastNotification";
import { useTheme } from "@/providers/ThemeProvider";

import { toSaveDefaultProp, useAISummary } from "./useAISummary";

import type { LanguageSelectorSaveDefault } from "@/components/molecules/LanguageSelector";

/**
 * Props for useAISummaryCard hook
 */
interface UseAISummaryCardProps {
  /** Player data fragment */
  player: PlayerDetailPlayerMetaFragment | null | undefined;
  /** Optional external isDark prop to combine with theme */
  externalIsDark?: boolean;
  /** Whether to generate a player summary or scouting summary */
  summaryType?: "player" | "scouting";
  /** Notes data for scouting summary prompt generation */
  notesData?: NotesListNotesByPlayerIdQuery;
  /** Whether the generate button should be disabled */
  isGenerateDisabled?: boolean;
}

/**
 * Return type for useAISummaryCard hook
 */
interface UseAISummaryCardReturn {
  /** Whether dark mode is active (combined from theme and external prop) */
  isDark: boolean;
  /** Whether full summary modal is open */
  isModalOpen: boolean;
  /** Display content (truncated) */
  displayContent: string;
  /** Full content for modal */
  fullContent: string;
  /** Whether summary is generating */
  isGenerating: boolean;
  /** Error message if any */
  error: string | null;
  /** Formatted last updated timestamp */
  lastUpdated: string | null;
  /** Whether to show read more link */
  showReadMore: boolean;
  /** The language a generation will run in. Never null. */
  selectedLanguage: string;
  /** The locale the picker should mark — undefined until something resolves. */
  pickerLocale: string | undefined;
  /** The save-as-default row, or undefined when there is no override to save. */
  saveAsDefault: LanguageSelectorSaveDefault | undefined;
  /** Selected language display label */
  selectedLanguageLabel: string;
  /** Array of supported languages */
  supportedLanguages: readonly { value: string; label: string }[];
  /** Handler to open the modal */
  handleReadMore: () => void;
  /** Handler to close the modal */
  handleModalClose: () => void;
  /** Handler to copy summary to clipboard */
  handleCopySummary: () => void;
  /** Generates the summary in the given language (SE-5570: no defer). */
  onSelectLanguage: (language: string) => void;
  /** Whether the generate trigger is disabled (generating or no data). */
  isButtonDisabled: boolean;
  /** Whether the generate button should be disabled due to no data */
  isGenerateDisabled: boolean;
}

/**
 * Hook providing shared functionality for AI summary card containers
 * @param props - Hook configuration
 * @param props.player - Player data fragment
 * @param props.externalIsDark - Optional external dark mode flag
 * @param props.summaryType - Whether to generate a player or scouting summary
 * @param props.notesData - Notes query data for scouting summary prompt
 * @param props.isGenerateDisabled - Whether the generate button should be disabled
 * @returns Object with state, handlers, and render functions
 */
export const useAISummaryCard = ({
  player,
  externalIsDark,
  summaryType = "player",
  notesData,
  isGenerateDisabled = false,
}: UseAISummaryCardProps): UseAISummaryCardReturn => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { resolvedTheme } = useTheme();
  const themeIsDark = resolvedTheme === "dark";
  const isDark = externalIsDark ?? themeIsDark;
  const toast = useToastNotification();

  const {
    displayContent,
    fullContent,
    isGenerating,
    error,
    lastUpdated,
    showReadMore,
    selectedLanguage,
    pickerLocale,
    selectedLanguageLabel,
    supportedLanguages,
    handleGenerateInLanguage,
    handleSaveLanguageAsDefault,
    saveDefaultState,
  } = useAISummary({ player, summaryType, notesData });

  const saveAsDefault = useMemo(
    () => toSaveDefaultProp(saveDefaultState, handleSaveLanguageAsDefault),
    [saveDefaultState, handleSaveLanguageAsDefault]
  );

  const handleReadMore = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleCopySummary = useCallback(async () => {
    try {
      await ExpoClipboard.setStringAsync(fullContent);
      toast.show({
        description: "Summary copied to clipboard",
        action: "success",
      });
    } catch (error) {
      console.warn("Failed to copy summary to clipboard:", error);
      toast.show({
        description: "Failed to copy to clipboard",
        action: "danger",
      });
    }
  }, [fullContent, toast]);

  // SE-5570: the SelectDropdown shell owns open/close, so the SE-3823 defer
  // that let the gluestack RN Modal finish closing before re-render is gone —
  // generate directly on selection.
  const onSelectLanguage = useCallback(
    (language: string) => handleGenerateInLanguage(language),
    [handleGenerateInLanguage]
  );

  const isButtonDisabled = isGenerating || isGenerateDisabled;

  return {
    isDark,
    isModalOpen,
    displayContent,
    fullContent,
    isGenerating,
    error,
    lastUpdated,
    showReadMore,
    selectedLanguage,
    pickerLocale,
    saveAsDefault,
    selectedLanguageLabel,
    supportedLanguages,
    handleReadMore,
    handleModalClose,
    handleCopySummary,
    onSelectLanguage,
    isButtonDisabled,
    isGenerateDisabled,
  };
};
